/**
 * ExecutiveLetterPage — TCP-2 (Template Cover Preview hotfix, 2026-05-18).
 *
 * Renders the optional "Executive Letter" / preface as a SEPARATE page
 * immediately after the cover, before the Table of Contents and §1.
 *
 * Before TCP-2 the same content was injected at the TOP of `data-page="1"`,
 * INSIDE the cover wrapper — which on the non-classic cover variants
 * crowded the title block (`HeroImageCover` is full-bleed; `MinimalCover`
 * has tight quiet space around the title). Promoting the letter to its
 * own page restores the symmetric layout discipline the four variants
 * were designed against.
 *
 * Print discipline:
 *   The wrapper is tagged with `print:break-before-page` so the canonical
 *   PDF + DOCX export pipelines (puppeteer + docx-render) put a page
 *   break ahead of the letter. Browser print mode respects the same
 *   Tailwind utility. On-screen the letter still reads as a separate
 *   page because the cover wrapper above it has a `border-b border-slate-100`
 *   rule and the letter wrapper carries an identical sidebar + padding shell.
 *
 * Back-compat invariants:
 *   - `data-testid="cover-overlay"` is preserved (the inline block kept
 *     the same testid pre-TCP).
 *   - `data-template-cover-overlay` boolean attribute kept for live-prod
 *     console probing.
 *   - `data-testid="cover-overlay-signature-render"` /
 *     `cover-overlay-dateline-render` kept verbatim — the existing
 *     pinned tests at `__tests__/SiteRagReportView.coverOverlay.test.tsx`
 *     continue to find them.
 *   - NEW `data-testid="executive-letter-page"` added so TCP-AUDIT can
 *     verify the separate-page rendering.
 */
import React from 'react';
import type { ReportTheme } from '../../../types/reportTheme';
import type { ReportTemplate } from '../../../types/reportTemplate';
import { EditorialSectionShell } from './EditorialSectionShell';
import { roleColor } from '../../../lib/reportRoleColors';
import {
  reportTypographySizes,
  REPORT_LINE_HEIGHT,
  reportHeadingInk,
} from '../../../lib/reportSectionHeading';

interface ExecutiveLetterPageProps {
  overlay: NonNullable<ReportTemplate['coverOverlay']>;
  theme: ReportTheme;
  /** When true, omit the confidential left rail (minimal / data-dense / hero). */
  omitSectionSidebar?: boolean;
  /**
   * TCP-3 (2026-06-05) — placement by cover type.
   *
   * Default (false) = FULL-PAGE behaviour: the letter is promoted to its
   * OWN page after a full-bleed (hero-image) cover, carrying the
   * `breakBefore: 'page'` style + `print:break-before-page` utility so the
   * PDF/print pipelines split a page ahead of it.
   *
   * When TRUE (`belowCover`) the cover is a non-full-page academic title
   * page (classic / boundary-plate / data-snapshot / masthead / minimal)
   * and the letter renders DIRECTLY BENEATH the cover on the SAME page
   * (page 1). In that mode the page-break is OMITTED here — the caller
   * instead forces the FIRST body section (executive summary) onto a fresh
   * page. All testids + theme styling are identical in both modes; only
   * the break + the on-screen separator hairline differ.
   */
  belowCover?: boolean;
}

export const ExecutiveLetterPage: React.FC<ExecutiveLetterPageProps> = ({
  overlay,
  theme,
  omitSectionSidebar = false,
  belowCover = false,
}) => {
  // PART K1 — the letter title routes through the shared type scale (h3
  // tier) instead of the scattered `text-base` literal, so it densifies +
  // tracks the theme alongside every other report heading. Keeps OUR
  // headingFont + headingColor (faces/colours unchanged).
  const sizes = reportTypographySizes(theme);
  return (
    <EditorialSectionShell
      data-page="executive-letter"
      data-testid="executive-letter-page"
      // Full-page mode keeps the page-break utility; below-cover mode drops
      // it so the letter flows under the title block on page 1.
      extraClassName={belowCover ? undefined : 'print:break-before-page'}
      style={
        belowCover
          ? { borderTop: `1px solid ${roleColor(theme, 'hair')}` }
          : { breakBefore: 'page', borderBottom: `1px solid ${roleColor(theme, 'hair')}` }
      }
      omitSectionSidebar={omitSectionSidebar}
      theme={theme}
    >
      {/* Editorial callout treatment — accent LEFT-rule + faint
          accent-soft wash, replacing the slate box. Theme-aware via
          roleColor so it follows the active preset. */}
      {/* William QA 2026-06-28 — left accent rule removed report-wide */}
      <div
        className="py-4"
        style={{
          background: roleColor(theme, 'accentSoft'),
        }}
        data-testid="cover-overlay"
        data-template-cover-overlay
      >
        <h3
          className="font-bold mb-2"
          style={{
            color: reportHeadingInk(theme),
            fontFamily: theme.headingFont,
            fontSize: sizes.h3,
            lineHeight: REPORT_LINE_HEIGHT.heading,
          }}
        >
          {overlay.title}
        </h3>
        <div className="text-sm" style={{ whiteSpace: 'pre-wrap', color: roleColor(theme, 'body') }}>
          {overlay.body}
        </div>
        {(overlay.signature || overlay.dateLine) && (
          <div className="mt-3 text-xs" style={{ color: roleColor(theme, 'muted') }}>
            {overlay.signature && (
              <div data-testid="cover-overlay-signature-render">{overlay.signature}</div>
            )}
            {overlay.dateLine && (
              <div data-testid="cover-overlay-dateline-render">{overlay.dateLine}</div>
            )}
          </div>
        )}
      </div>
    </EditorialSectionShell>
  );
};
